import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getAssignmentWithSubmissions } from '../../services/AssignmentServices';
import styles from './styles/ClassroomAssignmentSubmissions.module.css';
import blankProfileImage from '../../images/profileicon.png';
import { ClassroomContext } from '../../context/ClassroomContext';
import { FaMapMarkerAlt, FaCheckSquare, FaKeyboard } from 'react-icons/fa';

const ClassroomAssignmentSubmissions = () => {
    const { assignmentId } = useParams();
    const [assignment, setAssignment] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedId, setExpandedId] = useState(null);
    const { isTeacher } = useContext(ClassroomContext);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchAssignment = async () => {
            try {
                const data = await getAssignmentWithSubmissions(assignmentId);
                console.log(data);
                setAssignment(data);
                setLoading(false);
            } catch (error) {
                setError(error.response?.data?.error || error.message || 'Failed to load assignment');
                setLoading(false);
            }
        };

        fetchAssignment();
    }, [assignmentId]);


const toggleExpand = (id) => {
    setExpandedId(prev => (prev === id ? null : id));
};

const getDecisionClass = (decision) => {
    const classes = [styles.decisionItem];
    if (decision.type === 'choice' || decision.type === 'input') {
        if (decision.correctness === 'correct') {
            classes.push(styles.correct);
        } else if (decision.correctness === 'incorrect') {
            classes.push(styles.incorrect);
        }
    }
    return classes.join(' ');
};

    if (!isTeacher) {
        return (
            <div className={styles.permissionError}>
                You do not have permission to view this page.
            </div>
        );
    }

    if (loading) {
        return <div className={styles.loading}>Loading...</div>;
    }

    if (error) {
        return <div className={styles.error}>{error}</div>;
    }

    if (!assignment) {
        return <div className={styles.error}>No assignment data available</div>;
    }

    const handleVisualizeSubmissions = () => {
        navigate(
            `/my-classrooms/${assignment.classroom}/l/${assignment?.scenario?._id}/${assignmentId}`, 
            {
                state: { assignmentWithSubmissions: assignment },
                replace: true // This is a UX improvement, by replacing the current entry in the browser's history stack instead of adding a new one.
            }
        );
    };

    return (
        <div className={styles.container}>
            <div className={styles.assignmentDetails}>
                <h1>{assignment.title || 'Untitled Assignment'}</h1>
                <p className={styles.description}>{assignment.description || 'No description'}</p>
                <p>
                    <strong>Due Date:</strong>{' '}
                    {assignment.dueDate
                        ? new Date(assignment.dueDate).toLocaleDateString()
                        : 'No due date'}
                </p>
                <p>
                    <strong>Scenario:</strong> {assignment.scenario?.title || (!assignment.scenario ? "Unlinked Scenario" : 'Unnamed Scenario')}
                </p>
                <div className={styles.students}>
                    <strong>Students:</strong>
                    {assignment.students.length > 0 ? (
                        <ul>
                            {assignment.students.map((student) => (
                                <li key={student._id}>
                                    <img
                                        src={student.profilePicture || blankProfileImage}
                                        alt={student.username}
                                        className={styles.profilePicture}
                                    />
                                    {student.username}
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <span> No students assigned</span>
                    )}
                </div>
                {assignment.scenario && <button
                    className={styles.visualizeButton}
                    onClick={handleVisualizeSubmissions}
                >
                    Visualize Submissions
                </button>
                }
            </div>
            <div className={styles.submissions}>
            <h2>Submissions</h2>
            {assignment.submissions.length > 0 ? (
                <ul className={styles.submissionList}>
                    {assignment.submissions
                        .sort((a,b) => new Date(b.updatedAt) - new Date(a.updatedAt))
                        .map((submission) => {
                        const isExpanded = expandedId === submission._id;

                        return (
                        <li
                            key={submission._id}
                            className={styles.submissionItem}
                            onClick={() => toggleExpand(submission._id)}
                            style={{cursor: 'pointer'}}
                        >
                            <div className={styles.submissionHeader}>
                                <img
                                    src={submission.viewer.profilePicture || blankProfileImage}
                                    alt={submission.viewer.username}
                                    className={styles.profilePicture}
                                />
                                <span className={styles.username}>
                                    {submission.viewer.username}
                                </span>
                            </div>
                            <p>
                                <strong>Status:</strong>{' '}
                                {submission.isCompleted ? 'Completed' : 'In Progress'}
                            </p>
                            <p>
                                <strong>Submitted:</strong>{' '}
                                {new Date(submission.createdAt).toLocaleString()}
                            </p>
                            {submission.grade ? (
                                <div className={styles.gradeBox}>
                                    <p>
                                        <strong>Grade:</strong> {submission.grade}
                                    </p>
                                </div>
                            ) : (
                                submission.isCompleted ? <p><strong>Grade:</strong> No grades for this assignment</p>
                                : <p><strong>Grade:</strong> Not graded yet</p>
                            )}

                            {/* DECISION DROPDOWN */}
                            {isExpanded && (
                                submission.decisions && submission.decisions.length > 0 ? (
                                    <ul className={styles.decisionList}>
                                        {submission.decisions
                                            .filter(dec => dec.priority)
                                            .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
                                            .map((decision, idx) => (
                                            <li
                                                key={decision._id || idx}
                                                className={getDecisionClass(decision)}
                                            >
                                                {decision.type === 'situation' && (
                                                    <>
                                                        <FaMapMarkerAlt className={styles.decisionIcon} />
                                                        <span>Question: {decision.title || 'untitled'}</span>
                                                    </>
                                                )}
                                                {decision.type === 'choice' && (
                                                    <>
                                                        <FaCheckSquare className={styles.decisionIcon} />
                                                        <span>Choice: {decision.title || 'untitled'}</span>
                                                    </>
                                                )}
                                                {decision.type === 'input' && (
                                                    <>
                                                        <FaKeyboard className={styles.decisionIcon} />
                                                        <span>Input: "{decision.userInput}"</span>
                                                    </>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <div className={styles.noDecisions}>
                                        No decisions recorded in this submission.
                                    </div>
                                )
                            )}
                        </li>
                        );
                    })}
                </ul>
            ) : (
                <p>No submissions yet</p>
            )}
        </div>
        </div>
    );
};

export default ClassroomAssignmentSubmissions;